import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { completeSignIn } from '@/auth/auth-service';
import { setSession } from '@/auth/auth-store';
import { CallbackPage, type CallbackState } from '@/features/auth/callback-page';

/**
 * Retour du fournisseur d'identité : échange du code d'autorisation contre les jetons
 * (PKCE S256), puis navigation vers la cible mémorisée dans le `state` OIDC.
 *
 * Route distincte de /silent-callback : une iframe de renouvellement silencieux qui
 * atterrirait ici consommerait une seconde fois le même code (« Code already used »).
 */
export const Route = createFileRoute('/callback')({ component: CallbackRoute });

function CallbackRoute() {
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>('pending');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { session, returnTo } = await completeSignIn();
        if (cancelled) return;
        setSession(session);
        const target = returnTo.startsWith('/') ? returnTo : `/${session.tenantId}/overview`;
        await navigate({ to: target === '/' ? `/${session.tenantId}/overview` : target, replace: true });
      } catch {
        if (!cancelled) setState('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return <CallbackPage state={state} onRetry={() => void navigate({ to: '/login', replace: true })} />;
}
